import React, { useState, useEffect } from "react";
import { useRouter } from "next/router";

import TrendingImg from "@assets/icons/trending.svg";

import http from "@apiServices/http";
import urls from "@apiServices/apiUrls";
import Article from "@components/templates/Article";
import DiscoverMoreWidget from "@components/templates/DiscoverMore";
import Head from "next/head";
import NavigationBar from "@components/pages/ssrPage/NavigationBar";
import { getCookies } from "@utils/util";
// import { doc, getDoc, getFirestore, setDoc } from "firebase/firestore/lite";
// import { nanoid } from "nanoid";
import ArticleTracking from "@utils/articleTracking";
import ImageLoadingObserver from "@utils/imageLoadingObserver";
import { trackPageLoad } from "@components/pages/newsIdPage/ch-events";
import TrendingArticles from "@components/templates/TrendingArticles";
import { useTheme } from "next-themes";
import { serverErrorLogger } from "@analytics/ErrorLogger/server";
import dynamic from "next/dynamic";
import Link from "next/link";
import VideoArticle from "@components/templates/VideoArticle";
import firestoreService from "@firestoreServices/firestoreServices";
import { getCookie } from "cookies-next";

const EmptyStateView = dynamic(() => import("@components/templates/emptyStates/pageView"));

const NewsDetailsPage = ({
  handleBackClick,
  closeDownloadAppDrawer,
  noContentRelatedFeed,
  pageError = false,
  userLikeArticlesData,
  ...props
}: any) => {
  const router = useRouter();
  const { asPath, query } = router;
  const { id, news } = query;

  const data = props?.data || null;
  const trendingArticles = props.trendingArticles || [];
  const discoverMoreTags = props.discoverMoreTags || [];
  const [observer, setObserver] = useState(null);
  const [imageObserver, setImageObserver] = useState(null);
  const [seenArticleList, setSeenArticleList] = useState([]);
  const [seenArticle, setSeenArticle] = useState(null);
  const [readArticleList, setReadArticleList] = useState([]);
  const [readArticle, setReadArticle] = useState(null);
  const [userLikeArticles, setUserLikeArticles] = useState([]);

  const value: any = id;
  const title = data ? data.title : "";
  const summary = data ? data.summary : "";
  const description = summary ? summary.split("</li>")[0].slice(8) : null;
  const canonical = `${process.env.NEXT_PUBLIC_BASE_URL || "https://techminis.com"}${
    asPath.split("?")[0]
  }`;
  const { theme } = useTheme();

  useEffect(() => {
    const temp = [...seenArticleList];
    const readArticleListTemp = [...readArticleList];
    const articleTrackingObserver = new ArticleTracking(
      {
        page: "news_details",
        slug: news,
        newsId: id
      },
      (id) => {
        temp.push(id);
        setSeenArticle(id);
        setSeenArticleList(temp);
      },
      (id) => {
        readArticleListTemp.push(id);
        setReadArticle(id);
        setReadArticleList(readArticleListTemp);
      }
    );

    setObserver(articleTrackingObserver);
    // const imageLoadingObserver = new ImageLoadingObserver();
    // setImageObserver(imageLoadingObserver);

    return () => {
      articleTrackingObserver.destroy();
      // imageLoadingObserver.destroy();
    };
  }, []);

  useEffect(() => {
    const imageLoadingObserver = new ImageLoadingObserver();
    setImageObserver(imageLoadingObserver);

    return () => {
      imageLoadingObserver.destroy();
    };
  }, [theme]);

  const getUserFirestoreData = async () => {
    const firestore = new firestoreService();
    let userId = getCookie("techminis_u_id");
    let userData = await firestore.fetchUserData(userId);
    // const firestore = new firestoreService();
    // const userFirestoreData = await firestore.getUserData();
    setUserLikeArticles(userData?.likes || []);

    return userData;
  };

  useEffect(() => {
    getUserFirestoreData();
    const intervalId = setInterval(() => {
      if (window.__bgtracker) {
        trackPageLoad({ slug: news, article_id: id });
        clearInterval(intervalId);
      }
    }, 500);
  }, []);

  useEffect(() => {
    const container =
      document.getElementById("unsupported-browser-container") ||
      document.getElementById("container");

    container.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, [router.query.id]);

  // const imgToPreload = [];
  // if (data) {
  //   imgToPreload.push(data.mediaURL);
  // }
  // if (trendingArticles.length) {
  //   let count = 0;
  //   for (let i = 0; i < trendingArticles.length && count < 2; i++) {
  //     if (trendingArticles[i]?.mediaURL) {
  //       imgToPreload.push(trendingArticles[i].mediaURL);
  //       count++;
  //     }
  //   }
  // }

  let articleObj = {};
  let breadcrumbsObj = {};
  if (data) {
    const str = `${
      data
        ? (data?.summary || "")
            ?.replaceAll("<ul>", "")
            .replaceAll("<li>", "")
            .replaceAll("</li>", "")
            .replaceAll("</ul>", "")
        : ""
    }`;
    articleObj = {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      url: canonical,
      publisher: {
        "@type": "Organization",
        name: data.sourceName,
        logo: data.sourceLogo
      },
      headline: data.title,
      mainEntityOfPage: canonical,
      articleBody: str,
      image: [data.mediaURL],
      datePublished: data.publishedDate
    };

    breadcrumbsObj = {
      "@context": "http://schema.org/",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          item: { "@id": "/", name: "Home" }
        },
        {
          "@type": "ListItem",
          position: 2,
          item: {
            "@id": encodeURI(`/${data?.tagSlugInfoList[0].slug}`),
            name: data?.tagSlugInfoList[0].slugDisplayName
          }
        },
        {
          "@type": "ListItem",
          position: 3,
          item: {
            "@id": `/${data.slug}/${data.id}`,
            name: data.title
          }
        }
      ]
    };
  }
  return (
    <>
      <Head>
        <link rel="canonical" href={canonical || "https://techminis.com"} key="canonical" />
        <title>{title || "Techminis | Providing short summaries for crisp and quick read"}</title>
        <meta
          name="description"
          content={description || "Providing short summaries for crisp and quick read"}
        />
        {!!data && (
          <script
            type="application/ld+json"
            id="microdata-details-page"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(articleObj) }}
          />
        )}
        {!!data && (
          <script
            type="application/ld+json"
            id="microdata-breadcrumbs"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbsObj) }}
          />
        )}
        <meta property="og:url" content={canonical || "https://techminis.com"} />
        <meta
          property="og:title"
          content={title || "Techminis | Providing short summaries for crisp and quick read"}
          key="og:title"
        />
        <meta
          property="og:description"
          content={description || "Providing short summaries for crisp and quick read"}
          key="og:description"
        />
        {!!!data && <meta name="robots" content="noindex" key="robots" />}
        {data ? (
          <meta property="og:image" content={data.mediaURL} key="og:image" />
        ) : (
          <meta property="og:image" content="/brandingImage.png" />
        )}

        {/* Disabled for now */}
        {/* {imgToPreload.length
          ? imgToPreload.map((img, ind) => <link key={ind} rel="preload" as="image" href={img} />)
          : null} */}
      </Head>
      {pageError && (
        <EmptyStateView
          type="error500"
          className="h-full p-5"
          onClick={() => router.reload()}
          noNavigationBar={true}
        />
      )}
      {!pageError && (
        <>
          <NavigationBar
            onLogoClick={() => {
              if (
                window &&
                window.__bgHistory &&
                window.__bgHistory.lastLocation &&
                window.__bgHistory.lastLocation === "/naukri"
              ) {
                router.push(`/naukri?mode=naukri&showTrendingFeed=${true}`, "/naukri?mode=naukri");
              } else {
                router.push(`/?showTrendingFeed=${true}`, "/");
              }
            }}
            handleOnSignup={() => {}}
          />
          {data && data?.tagSlugInfoList?.length && (
            <div className="flex w-full items-center bg-N100 px-5 pt-2 dark:bg-DN050">
              <Link href={"/"} passHref prefetch={false}>
                <a
                  title="home"
                  className="text-readingTextSmall  font-bold leading-readingTextSmall text-N900 line-clamp-3 dark:text-DN900"
                >
                  <span className="mr-2">Home</span>
                </a>
              </Link>
              <p className="mr-1.5 text-readingTextSmall  font-bold leading-readingTextSmall text-N900 line-clamp-3 dark:text-DN900">
                &gt;
              </p>

              <Link href={`/${data?.tagSlugInfoList[0].slug}`} passHref prefetch={false}>
                <a
                  title={data?.tagSlugInfoList[0].slugDisplayName}
                  className="text-readingTextSmall font-bold leading-readingTextSmall text-N900 line-clamp-3 dark:text-DN900"
                >
                  <span className="mr-2">{data?.tagSlugInfoList[0].slugDisplayName}</span>
                </a>
              </Link>
              <p className="mr-1.5 text-readingTextSmall  font-bold leading-readingTextSmall text-N900 line-clamp-3 dark:text-DN900">
                &gt;
              </p>
              <span className="white-nowrap mr-2 text-readingTextSmall font-medium  leading-readingTextSmall text-N500 line-clamp-1 dark:text-N100">
                {data.title.length > 20 ? `${data.title.slice(0, 10)}...` : data.title}
              </span>
            </div>
          )}
          <div>
            <div className="mx-auto my-0 bg-N100 dark:bg-DN050">
              {data ? (
                <>
                  {data.newsBiteType == "NEWS_WRAP" && (
                    <VideoArticle
                      key={data.id}
                      index={data.id}
                      articleData={data}
                      className="mb-5"
                      userLikeArticles={userLikeArticlesData}
                    />
                  )}
                  {data.newsBiteType == "SHORT_ARTICLE" && (
                    <Article
                      index={data.id}
                      className="mt-5 flex h-full w-full cursor-pointer flex-col justify-between border-b border-P100 bg-N100 duration-300 dark:border-DG700 dark:bg-DN050"
                      closeDownloadAppDrawer={closeDownloadAppDrawer}
                      key={data.id}
                      isTrending={false}
                      userLikeArticles={userLikeArticles}
                      articleData={data}
                      removeNotInterestedArticle={() => {}}
                      hideFollowOptionFromThreeDotDrawer={true}
                      isSourceHidden
                      savedKeyword={value}
                      toggleFollowBtn={() => {}}
                      isFollowing={false}
                      showFullDescription={true}
                      observer={observer}
                      lazyLoadImage={false}
                      imageObserver={imageObserver}
                      slug={news.toString()}
                      page="news_details"
                      articleSeen={seenArticle === data.id}
                      seenArticleList={seenArticleList}
                      articleRead={readArticle === data.id}
                    />
                  )}
                </>
              ) : (
                <div className="z-10 flex w-full flex-col items-start bg-N100 px-5 py-5 dark:bg-DN050">
                  <div className="flex w-full flex-col justify-between bg-N100 transition-none dark:bg-DN050">
                    <p className="font-normal text-readingTextMedium leading-readingTextMedium text-N500 dark:text-DN500">
                      No such article found
                    </p>
                  </div>
                </div>
              )}
              {trendingArticles.length > 0 && (
                <TrendingArticles trendingArticleList={trendingArticles} page="news_details" />
              )}
              {discoverMoreTags.length && (
                <div className="flex h-full w-full cursor-pointer flex-col justify-between overflow-hidden border-b border-P100 bg-N100 px-5 duration-300 dark:border-DG700 dark:bg-DN050">
                  <DiscoverMoreWidget
                    discoverMoreTags={discoverMoreTags || []}
                    observer={observer}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="pos_right absolute bottom-9 z-[12] flex">
            <button
              className="flex h-[44px] w-[44px] items-center justify-center rounded-full bg-P500 p-3 shadow-customBottom shadow-[#0204092e]"
              onClick={() => {
                if (
                  window &&
                  window.__bgHistory &&
                  window.__bgHistory.lastLocation &&
                  window.__bgHistory.lastLocation === "/naukri"
                ) {
                  router.push(
                    `/naukri?mode=naukri&showTrendingFeed=${true}`,
                    "/naukri?mode=naukri"
                  );
                } else {
                  router.push(`/?showTrendingFeed=${true}`, "/");
                }
              }}
            >
              <img src={TrendingImg} alt="trending" />
            </button>
          </div>
        </>
      )}
    </>
  );
};

export async function getServerSideProps(context) {
  const { query, req, res } = context;
  try {
    const cookies = getCookies({ req, res });
    const firestore = new firestoreService();
    let userId = cookies["techminis_u_id"];
    let userData = await firestore.fetchUserData(userId, true, { req, res });
    // const db = getFirestore();
    // let userId = cookies["id"];
    // let userData;
    // if (userId) {
    //   const docRef = doc(db, "users", userId);
    //   const docSnap = userId ? await getDoc(docRef) : null;

    //   if (docSnap?.exists() && docSnap.data()) {
    //     userData = docSnap.data();
    //   }
    // } else {
    //   userId = nanoid();
    //   setCookie("id", userId, { req, res });
    //   userData = {
    //     id: userId,
    //     platform: "web",
    //     lastUpdatedDate: new Date(),
    //     preferences: [],
    //     savedKeywords: []
    //   };

    //   const userDetail = doc(db, "users", userId);
    //   await setDoc(userDetail, userData, { merge: true });
    // }
    let data = null;

    const fetchArticleData = async () => {
      try {
        const url = urls.getArticle({ articleId: query.id, slug: encodeURI(query.news) });
        data = await http.get(url, {
          headers: {
            uid: userData.id
          }
        });
      } catch (error) {}
    };

    let trendingArticles = [];
    const fetchTrendingAricles = async () => {
      return http
        .post(
          urls.trendingArticles,
          { tags: userData?.preferences || [] },
          { headers: { uid: userData.id } }
        )
        .then((res: any) => {
          trendingArticles = res || [];
        })
        .catch(() => {});
    };

    let discoverMoreTags = [];
    const fetchDiscoverMoreTags = async () => {
      let url = urls.trendingDiscoverMoreTags;
      return http
        .get(url, { headers: { uid: userData.id } })
        .then((res: any) => {
          if (res.primary && res.primary.length) {
            discoverMoreTags = res.primary.map((i) => [i.slug, i.slugDisplayName]);
          } else {
            if (res.secondary && res.secondary.length) {
              discoverMoreTags = res.secondary.map((i) => [i.slug, i.slugDisplayName]);
            }
          }
        })
        .catch(() => {})
        .finally(() => {});
    };

    await Promise.allSettled([fetchArticleData(), fetchTrendingAricles(), fetchDiscoverMoreTags()]);

    // filter out the current article from trending articles
    if (data) {
      trendingArticles = trendingArticles.filter((i) => i.id !== data.id);
    } else {
      res.statusCode = 410;
    }
    return {
      props: { data, trendingArticles, discoverMoreTags, userLikeArticlesData: [] }
    };
  } catch (err) {
    serverErrorLogger({ err, req, screenName: "news_details" });
    return {
      props: { pageError: true }
    };
  }
}

export default NewsDetailsPage;
